import { BenefitWithPlanInfo } from '@wix/ambassador-pricing-plans-v2-benefit/types';
import {
  isServiceOfferedAsPricingPlan,
  PriceUtils,
} from '@wix/bookings-uou-mappers';
import { Experiments } from '@wix/yoshi-flow-editor';
import { PaymentOption, TFunction } from '../../../../types/types';
import { DateTimeFormatter } from '@wix/bookings-date-time';
import { SlotService } from '../../../state/types';
import {
  createBuyPlanOption,
  createCustomPriceOption,
  createFreeOption,
  createPayOfflineOption,
  createPayOnlineOption,
  createUsePlanOption,
  isOfferedAsOneTime,
} from '..';
import { SelectedPaymentOption } from '../../../../types/ambassador/bookings/ambassador-bookings-v2-booking';
import { ExperimentsConsts } from '../../../../consts/experiments';

export const getSlotServicePaymentOptions = ({
  slotService,
  benefitsWithPlanInfo,
  numberOfParticipants,
  isPricingPlanInstalled,
  t,
  dateAndTimeFormatter,
  dateRegionalSettingsLocale,
  experiments,
}: {
  slotService: SlotService;
  benefitsWithPlanInfo?: BenefitWithPlanInfo[];
  numberOfParticipants: number;
  isPricingPlanInstalled: boolean;
  t: TFunction;
  dateAndTimeFormatter: DateTimeFormatter;
  dateRegionalSettingsLocale: string;
  experiments: Experiments;
}): PaymentOption[] => {
  const options: PaymentOption[] = slotService.memberships?.eligibleMemberships
    ?.length
    ? slotService.memberships.eligibleMemberships!.map((membership) => {
        return createUsePlanOption(
          membership,
          numberOfParticipants,
          benefitsWithPlanInfo,
          t,
          dateAndTimeFormatter,
        );
      })
    : [];

  const showBuyAPricingPlan = isServiceOfferedAsPricingPlan(
    slotService.service.payment,
    isPricingPlanInstalled,
  );

  if (options.length === 0 && showBuyAPricingPlan) {
    slotService.possiblePlans?.forEach((plan) => {
      options.push(createBuyPlanOption(plan));
    });
  }
  const showPaySingleSession = isOfferedAsOneTime(slotService.service.payment);

  if (showPaySingleSession) {
    let price = slotService.paymentDetails.price;
    if (slotService.dynamicPriceInfo?.selectedVariants?.length) {
      const selectedVariant = slotService.dynamicPriceInfo?.selectedVariants[0];

      const priceString =
        slotService.dynamicPriceInfo?.serviceOptionsAndVariants?.variants?.values?.find(
          (value) =>
            value.choices?.find(
              (choice) =>
                (choice.custom &&
                  choice.custom === selectedVariant?.choices?.[0].custom) ||
                (choice.staffMemberId &&
                  choice.staffMemberId ===
                    selectedVariant?.choices?.[0].staffMemberId),
            ),
        )?.price?.value!;

      price = parseInt(priceString, 10);
    }
    if (
      slotService.service.paymentTypes.includes(SelectedPaymentOption.ONLINE)
    ) {
      options.push(
        createPayOnlineOption(
          PriceUtils.getFormattedCurrency({
            price,
            currency: slotService.paymentDetails.currency,
            locale: dateRegionalSettingsLocale,
            currencyDisplay: 'narrowSymbol',
          }),
          t,
          experiments,
        ),
      );
    }
    if (
      slotService.service.paymentTypes.includes(SelectedPaymentOption.OFFLINE)
    ) {
      if (
        experiments.enabled(ExperimentsConsts.ShowPaymentDropdownWithSections)
      ) {
        const isFree = slotService.service.payment.paymentDetails.isFree;
        const customPrice =
          slotService.service.payment.paymentDetails.priceText;
        if (isFree) {
          if (customPrice) {
            options.push(createCustomPriceOption(customPrice));
          } else {
            options.push(createFreeOption());
          }
        } else {
          options.push(
            createPayOfflineOption(
              PriceUtils.getFormattedCurrency({
                price,
                currency: slotService.paymentDetails.currency,
                locale: dateRegionalSettingsLocale,
                currencyDisplay: 'narrowSymbol',
              }),
              t,
              experiments,
            ),
          );
        }
      } else {
        options.push(
          createPayOfflineOption(
            PriceUtils.getFormattedCurrency({
              price,
              currency: slotService.paymentDetails.currency,
              locale: dateRegionalSettingsLocale,
              currencyDisplay: 'narrowSymbol',
            }),
            t,
            experiments,
          ),
        );
      }
    }
  }

  return options;
};
